import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Quote, Building2 } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import { CTA } from "@/components/CTA";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import {
  SERVICES, INDUSTRIES, TECH_MARQUEE, PROCESS, WHY_US, SOLUTIONS,
  CASE_STUDIES, TESTIMONIALS, FAQS, COMPANY,
} from "@/lib/content";

const HERO_LINES = ["Enterprise integration,", "engineered for", "what's next."];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 120]);
  return (
    <section className="relative overflow-hidden pt-36 sm:pt-40 md:pt-52 pb-24 md:pb-32 px-6 md:px-12 lg:px-24" data-testid="hero">
      <div className="absolute inset-0 grid-lines opacity-[0.5] [mask-image:radial-gradient(ellipse_at_top,black,transparent_65%)]" />
      <div className="glow brand-gradient-bg opacity-[0.18] h-[34rem] w-[34rem] -top-44 -left-24" />
      <div className="glow h-[26rem] w-[26rem] top-20 right-0 opacity-[0.18]" style={{ background: "var(--brand-accent)" }} />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs md:text-sm font-medium"
          >
            <span className="h-2 w-2 rounded-full brand-gradient-bg animate-pulse" />
            IT Consulting &amp; Digital Transformation
          </motion.span>

          <h1 className="mt-7 font-display font-semibold tracking-tighter leading-[0.95] text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[5.5rem]">
            {HERO_LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 2 ? "brand-gradient-text" : ""}`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed"
          >
            We help enterprises connect their systems, modernize on the cloud and
            put AI to work — so technology stops being a constraint and starts
            compounding as an advantage. Senior teams, measurable outcomes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <CTA to="/contact" testId="hero-primary-cta">Book a consultation</CTA>
            <CTA to="/services" variant="outline" testId="hero-secondary-cta">Explore services</CTA>
          </motion.div>
        </div>

        <motion.div style={{ y }} className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[2rem] overflow-hidden border border-border shadow-2xl"
            style={{ clipPath: "polygon(0 0, 100% 0, 100% 88%, 88% 100%, 0 100%)" }}
          >
            <img
              src="https://images.pexels.com/photos/10325707/pexels-photo-10325707.png?auto=compress&cs=tinysrgb&w=1200"
              alt="Abstract enterprise network connectivity"
              className="w-full h-[26rem] object-cover"
              width="720"
              height="416"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="absolute -bottom-6 -left-6 glass rounded-2xl p-5 max-w-[15rem] shadow-xl"
          >
            <p className="font-display text-3xl font-semibold brand-gradient-text">API-led</p>
            <p className="text-sm text-muted-foreground mt-1">architecture that turns integration into reusable assets.</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const TechRibbon = () => (
  <section className="py-10 border-y border-border" data-testid="tech-ribbon">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-6">
      <p className="text-xs uppercase tracking-[0.25em] font-semibold text-muted-foreground font-mono text-center">
        Technologies we engineer with
      </p>
    </div>
    <div className="mask-fade-x">
      <Marquee speed={40} gradient={false} pauseOnHover>
        {TECH_MARQUEE.map((t, i) => (
          <span key={i} className="mx-8 font-display text-2xl md:text-3xl font-medium text-muted-foreground/70 hover:text-primary transition-colors">
            {t}
          </span>
        ))}
      </Marquee>
    </div>
  </section>
);

const SectionLabel = ({ children }) => (
  <span className="eyebrow">{children}</span>
);

const ServicesSection = () => (
  <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24" data-testid="services-overview">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-3xl mb-16">
        <Reveal><SectionLabel>What we do</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display font-semibold tracking-tight text-4xl md:text-5xl lg:text-6xl text-balance">
            A full-stack consultancy for the composable enterprise
          </h2>
        </Reveal>
      </div>
      <StaggerGroup className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {SERVICES.map((s, idx) => {
          const Icon = s.icon;
          return (
            <StaggerItem key={s.slug} className={s.span}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="card-premium group h-full flex flex-col justify-between p-7 md:p-8"
              >
                <div>
                  <div className="flex items-start justify-between">
                    <div className="icon-badge h-12 w-12 mb-6">
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <span className="font-mono text-sm text-muted-foreground/60 tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed max-w-lg">{s.excerpt}</p>
                </div>
                <div className="mt-7 flex flex-wrap gap-2">
                  {s.capabilities.slice(0, 4).map((c) => (
                    <span key={c} className="rounded-full border border-border px-3 py-1 text-xs font-medium text-muted-foreground">{c}</span>
                  ))}
                </div>
              </Link>
            </StaggerItem>
          );
        })}
      </StaggerGroup>
    </div>
  </section>
);

const IndustriesSection = () => (
  <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24 bg-muted/40" data-testid="industries-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal><SectionLabel>Industries</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display font-semibold tracking-tight text-4xl md:text-5xl">
              Deep expertise across regulated, complex sectors
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              We understand the constraints of highly regulated environments — and design accordingly.
            </p>
            <CTA to="/industries" variant="ghost" className="mt-6 px-0">View all industries</CTA>
          </Reveal>
        </div>
        <StaggerGroup className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
          {INDUSTRIES.map((ind) => {
            const Icon = ind.icon;
            return (
              <StaggerItem key={ind.name}>
                <div className="card-premium group p-6 h-full">
                  <div className="icon-badge h-11 w-11 mb-4">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-display text-xl font-semibold">{ind.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{ind.desc}</p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </div>
  </section>
);

const ProcessSection = () => (
  <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24" data-testid="process-section">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-3xl mb-16">
        <Reveal><SectionLabel>How we work</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-4xl md:text-5xl lg:text-6xl">
            A disciplined path from ambition to impact
          </h2>
        </Reveal>
      </div>
      <div className="divide-y divide-border border-t border-border">
        {PROCESS.map((p, i) => (
          <Reveal key={p.no} delay={i * 0.05}>
            <div className="group grid md:grid-cols-12 gap-6 py-8 md:py-11 items-baseline transition-colors hover:bg-muted/40 px-3 -mx-3 rounded-xl">
              <div className="md:col-span-2 font-mono text-6xl md:text-7xl font-semibold text-foreground/[0.12] group-hover:text-primary/30 transition-colors tabular-nums">{p.no}</div>
              <h3 className="md:col-span-4 font-display text-2xl md:text-3xl font-semibold tracking-tight">{p.title}</h3>
              <p className="md:col-span-6 text-muted-foreground leading-relaxed text-lg">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhySection = () => (
  <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24 bg-muted/40" data-testid="why-section">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-3xl mb-16">
        <Reveal><SectionLabel>Why FusionTech</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-4xl md:text-5xl lg:text-6xl">
            Senior engineering, enterprise discipline
          </h2>
        </Reveal>
      </div>
      <StaggerGroup className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {WHY_US.map((w) => {
          const Icon = w.icon;
          return (
            <StaggerItem key={w.title}>
              <div className="card-premium group p-7 h-full">
                <div className="icon-badge h-12 w-12 mb-5">
                  <Icon className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-xl font-semibold">{w.title}</h3>
                <p className="mt-2 text-muted-foreground leading-relaxed">{w.desc}</p>
              </div>
            </StaggerItem>
          );
        })}
      </StaggerGroup>
    </div>
  </section>
);

const SolutionsSection = () => (
  <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24" data-testid="solutions-section">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-3xl mb-16">
        <Reveal><SectionLabel>Featured solutions</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-4xl md:text-5xl lg:text-6xl">
            Accelerators that shorten time-to-value
          </h2>
        </Reveal>
      </div>
      <StaggerGroup className="grid md:grid-cols-3 gap-6">
        {SOLUTIONS.map((s) => (
          <StaggerItem key={s.title}>
            <div className="card-premium group p-8 h-full">
              <div className="glow brand-gradient-bg opacity-0 group-hover:opacity-20 h-40 w-40 -top-10 -right-10 transition-opacity duration-500" />
              <span className="relative rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold">{s.tag}</span>
              <h3 className="relative mt-5 font-display text-2xl font-semibold tracking-tight">{s.title}</h3>
              <p className="relative mt-3 text-muted-foreground leading-relaxed">{s.desc}</p>
              <div className="relative mt-6 flex items-center gap-2 text-sm font-semibold text-primary">
                Learn more <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
            </div>
          </StaggerItem>
        ))}
      </StaggerGroup>
    </div>
  </section>
);

const CaseStudiesSection = () => (
  <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24 bg-muted/40" data-testid="case-studies-preview">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
        <div className="max-w-2xl">
          <Reveal><SectionLabel>Case studies</SectionLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display font-semibold tracking-tight text-4xl md:text-5xl lg:text-6xl">
              Outcomes that speak for themselves
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}><CTA to="/case-studies" variant="outline">All case studies</CTA></Reveal>
      </div>
      <StaggerGroup className="grid md:grid-cols-2 gap-6">
        {CASE_STUDIES.slice(0, 2).map((c) => (
          <StaggerItem key={c.slug}>
            <Link to={`/case-studies/${c.slug}`} className="card-premium group block overflow-hidden">
              <div className="relative h-60 overflow-hidden">
                <img src={c.image} alt={c.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                <span className="absolute top-4 left-4 rounded-full glass px-3 py-1 text-xs font-semibold text-white">{c.industry}</span>
              </div>
              <div className="p-7">
                <h3 className="font-display text-2xl font-semibold tracking-tight">{c.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{c.summary}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">Read case study <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" /></span>
              </div>
            </Link>
          </StaggerItem>
        ))}
      </StaggerGroup>
      <p className="mt-6 text-xs text-muted-foreground font-mono">{/* Placeholder projects — replace with real client case studies. */}Illustrative engagements shown as placeholders.</p>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-3xl mb-16">
        <Reveal><SectionLabel>Voices</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display font-semibold tracking-tight text-4xl md:text-5xl lg:text-6xl text-balance">
            In our clients’ words
          </h2>
        </Reveal>
      </div>
      <StaggerGroup className="grid md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <StaggerItem key={i}>
            <div className="relative rounded-2xl border border-border bg-card p-8 h-full flex flex-col shadow-soft">
              <Quote className="h-9 w-9 text-primary/25" />
              <p className="mt-5 text-lg leading-relaxed flex-1 text-foreground/90">{t.quote}</p>
              <div className="mt-7 flex items-center gap-3 pt-6 border-t border-border">
                <div className="icon-badge h-11 w-11 rounded-full">
                  <Building2 className="h-5 w-5" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            </div>
          </StaggerItem>
        ))}
      </StaggerGroup>
      <p className="mt-6 text-xs text-muted-foreground font-mono">{/* Placeholder testimonials — replace with real client quotes. */}Placeholder testimonials shown for layout.</p>
    </div>
  </section>
);

const FaqSection = () => (
  <section className="py-28 md:py-40 px-6 md:px-12 lg:px-24 bg-muted/40" data-testid="faq-section">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-4">
        <Reveal><SectionLabel>FAQ</SectionLabel></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-display font-semibold tracking-tight text-4xl md:text-5xl">
            Questions, answered
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Can’t find what you’re looking for? <Link to="/contact" className="text-primary underline underline-offset-4">Talk to us</Link>.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-8">
        <Reveal>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-border">
                <AccordionTrigger className="text-left font-display text-lg md:text-xl font-medium hover:no-underline hover:text-primary">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="px-6 md:px-12 lg:px-24 pb-28 md:pb-40" data-testid="cta-band">
    <div className="relative max-w-7xl mx-auto overflow-hidden rounded-[2.5rem] brand-gradient-bg px-8 md:px-16 py-16 md:py-24 text-center">
      <div className="noise opacity-10" />
      <Reveal>
        <h2 className="font-display font-semibold tracking-tighter text-white text-4xl md:text-6xl leading-[0.95] max-w-3xl mx-auto text-balance">
          Let’s talk about the outcome you’re chasing
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-6 text-white/80 text-lg max-w-xl mx-auto">
          Book a consultation with a senior practitioner. We’ll listen first, then map a pragmatic first step — no hard sell.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-9 flex justify-center">
          <CTA to="/contact" className="bg-white !text-slate-900 shadow-xl" testId="cta-band-button">
            Book a consultation
          </CTA>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: COMPANY.name,
    url: COMPANY.url,
    description: "Enterprise integration, cloud, AI and custom software consulting.",
    email: COMPANY.email,
    areaServed: "Global",
  };
  return (
    <>
      <SEO
        title="Enterprise IT Consulting & Digital Transformation"
        description="FusionTech Consulting helps enterprises integrate systems, adopt AI and modernize on the cloud with senior-led teams and API-led architecture."
        path="/"
        schema={schema}
      />
      <Hero />
      <TechRibbon />
      <ServicesSection />
      <IndustriesSection />
      <ProcessSection />
      <WhySection />
      <SolutionsSection />
      <CaseStudiesSection />
      <TestimonialsSection />
      <FaqSection />
      <CtaBand />
    </>
  );
}
